import { ReactNode } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Loader2 } from 'lucide-react'

interface FormLayoutProps {
  /** Form title */
  title: string
  /** Form description */
  description?: string
  /** Form fields to render */
  children: ReactNode
  /** Form submission handler */
  onSubmit: (e: React.FormEvent) => void
  /** Cancel button handler */
  onCancel: () => void
  /** Submit button text */
  submitText?: string | ReactNode
  /** Cancel button text */
  cancelText?: string
  /** Loading state */
  loading?: boolean
  /** Disable submit button */
  submitDisabled?: boolean
  /** Additional CSS classes for the card */
  className?: string
  /** Custom action buttons to display before submit */
  customActions?: ReactNode
}

export function FormLayout({
  title,
  description,
  children,
  onSubmit,
  onCancel,
  submitText = 'Submit',
  cancelText = 'Cancel',
  loading = false,
  submitDisabled = false,
  className,
  customActions,
}: FormLayoutProps) {
  return (
    <Card className={`${className} mt-6`}>
      {(title || description) && (
        <CardHeader>
          {title && <CardTitle>{title}</CardTitle>}
          {description && <CardDescription>{description}</CardDescription>}
        </CardHeader>
      )}
      <CardContent>
        <form onSubmit={onSubmit} className="space-y-6">
          {children}

          <div className="flex flex-col sm:flex-row gap-4 pt-4">
            <Button type="submit" disabled={loading || submitDisabled} className="flex-1 w-full">
              {typeof submitText === 'string' ? (
                <>
                  {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  {submitText}
                </>
              ) : (
                submitText
              )}
            </Button>
            {customActions}
            <Button
              type="button"
              variant="outline"
              onClick={onCancel}
              disabled={loading}
              className="flex-1 w-full"
            >
              {cancelText}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  )
}
